/** Financials tab engine (pure; node-tested): history → forecast → ABS, drivers, edits, fit. */

export const DRIVERS = [
  { key: 'volumeMult', label: 'Production volume ×', min: 0.5, max: 1.5, step: 0.01, fmt: (v) => `${v.toFixed(2)}×` },
  { key: 'declineAdjPct', label: 'Extra decline (%/yr)', min: -10, max: 20, step: 0.5, fmt: (v) => `${v.toFixed(1)}%` },
  { key: 'priceMult', label: 'Realized price ×', min: 0.5, max: 1.5, step: 0.01, fmt: (v) => `${v.toFixed(2)}×` },
  { key: 'loeMult', label: 'LOE ×', min: 0.5, max: 1.75, step: 0.01, fmt: (v) => `${v.toFixed(2)}×` },
  { key: 'taxRatePct', label: 'Production taxes (% rev)', min: 0, max: 15, step: 0.1, fmt: (v) => `${v.toFixed(1)}%` },
  { key: 'capexK', label: 'Capex ($k / month)', min: 0, max: 200, step: 5, fmt: (v) => `$${v.toFixed(0)}k` },
];

export const EDITABLE_LINES = ['production', 'price', 'loe', 'capex'];

export function quarterOf(month) {
  return `${month.slice(0, 4)}-Q${Math.floor((Number(month.slice(5, 7)) - 1) / 3) + 1}`;
}

export function baseTaxRatePct(months) {
  const f = months.filter((m) => m.kind === 'forecast');
  const rev = f.reduce((a, m) => a + (m.revenue || 0), 0);
  const tax = f.reduce((a, m) => a + (m.taxes || 0), 0);
  return rev > 0 ? (tax / rev) * 100 : 0;
}

export function presetDrivers(months, volumeMult = 1) {
  return { volumeMult, declineAdjPct: 0, priceMult: 1, loeMult: 1, taxRatePct: +baseTaxRatePct(months).toFixed(2), capexK: 0 };
}

/** Months with forecast lines recomputed from drivers + per-quarter overrides. */
export function applyDrivers(months, drivers, overrides = {}) {
  const f0 = months.findIndex((m) => m.kind === 'forecast');
  const out = months.map((m, i) => {
    if (m.kind !== 'forecast') return { ...m };
    const t = (i - f0) / 12;
    const vol = (m.netMcfe || 0) * drivers.volumeMult * (1 - drivers.declineAdjPct / 100) ** t;
    const price = (m.realizedPrice || 0) * drivers.priceMult;
    const loe = (m.loe || 0) * drivers.loeMult;
    const capex = (m.capex || 0) + drivers.capexK * 1000;
    return { ...m, netMcfe: vol, realizedPrice: price, loe, capex, _base: m };
  });
  // overrides: quarter totals (production, loe, capex) or average (price)
  const byQ = {};
  out.forEach((m, i) => {
    if (m.kind === 'forecast') (byQ[quarterOf(m.month)] ||= []).push(i);
  });
  for (const [k, v] of Object.entries(overrides)) {
    const [line, q] = k.split('|');
    const idx = byQ[q];
    if (!idx || v == null || !Number.isFinite(v)) continue;
    if (line === 'price') idx.forEach((i) => { out[i].realizedPrice = v; });
    else {
      const field = line === 'production' ? 'netMcfe' : line;
      const scale = line === 'production' ? 1e6 : 1e3; // MMcfe, $k
      const tot = idx.reduce((a, i) => a + (out[i][field] || 0), 0);
      idx.forEach((i) => { out[i][field] = tot > 0 ? (out[i][field] / tot) * v * scale : (v * scale) / idx.length; });
    }
    idx.forEach((i) => { out[i].edited = [...(out[i].edited || []), line]; });
  }
  for (const m of out) {
    if (m.kind !== 'forecast') continue;
    m.revenue = m.netMcfe * m.realizedPrice;
    m.taxes = m.revenue * (drivers.taxRatePct / 100);
    m.ncf = m.revenue + (m.hedge || 0) - m.loe - m.taxes - m.capex;
    const ds = (m.absInterest || 0) + (m.absSeniorFees || 0) + (m.absScheduled || 0);
    m.absDebtService = m.absInterest == null ? null : ds;
    m.dscr = ds > 0 ? m.ncf / ds : null;
  }
  return out;
}

const SUM = ['grossMcf', 'netMcfe', 'revenue', 'hedge', 'loe', 'taxes', 'capex', 'ncf', 'overhead', 'absInterest', 'absSeniorFees', 'absScheduled', 'absToParent', 'absDebtService'];

/** Quarterly roll-up; a field is null (missing) if any month in the quarter lacks it. */
export function quarterize(months) {
  const qs = [];
  for (const m of months) {
    const q = quarterOf(m.month);
    let cur = qs[qs.length - 1];
    if (!cur || cur.quarter !== q) {
      cur = { quarter: q, months: [], kinds: new Set(), edited: new Set() };
      qs.push(cur);
    }
    cur.months.push(m);
    cur.kinds.add(m.kind);
    (m.edited || []).forEach((e) => cur.edited.add(e));
  }
  return qs.map((c) => {
    const r = { quarter: c.quarter, kind: c.kinds.size === 1 ? [...c.kinds][0] : c.kinds.has('forecast') ? 'forecast' : 'actual', mixed: c.kinds.size > 1, kindsLabel: ['actual', 'gap', 'forecast'].filter((x) => c.kinds.has(x)).join(' + ').toUpperCase(), edited: [...c.edited], n: c.months.length };
    for (const f of SUM) r[f] = c.months.every((m) => m[f] != null) ? c.months.reduce((a, m) => a + m[f], 0) : null;
    r.realizedPrice = r.revenue != null && r.netMcfe ? r.revenue / r.netMcfe : null;
    r.absNotes = c.months[c.months.length - 1].absNotes ?? null;
    r.dscr = r.absDebtService ? r.ncf / r.absDebtService : null;
    for (const s of ['base', 'upside', 'downside']) {
      r[`ncf_${s}`] = c.months.every((m) => m.scenarioNcf?.[s] != null) ? c.months.reduce((a, m) => a + m.scenarioNcf[s], 0) : null;
    }
    r.missing = [...new Set(c.months.flatMap((m) => m.missing || []))];
    return r;
  });
}

/** Driver implied by an edit vs the un-overridden driver value for that quarter. */
export function impliedDriver(months, drivers, line, quarter, value) {
  const q = quarterize(applyDrivers(months, { ...drivers, ...(line === 'production' ? { volumeMult: 1 } : line === 'price' ? { priceMult: 1 } : line === 'loe' ? { loeMult: 1 } : { capexK: 0 }) })).find((x) => x.quarter === quarter);
  if (!q) return null;
  if (line === 'production') return q.netMcfe ? { key: 'volumeMult', value: (value * 1e6) / q.netMcfe } : null;
  if (line === 'price') return q.realizedPrice ? { key: 'priceMult', value: value / q.realizedPrice } : null;
  if (line === 'loe') return q.loe ? { key: 'loeMult', value: (value * 1e3) / q.loe } : null;
  return { key: 'capexK', value: Math.max(0, (value * 1e3 - (q.capex || 0)) / q.n / 1e3) };
}

/** Re-solve sliders to the user's edits. Production with ≥2 edited quarters fits volume × and decline. */
export function fitDriversToEdits(months, drivers, overrides) {
  const next = { ...drivers };
  const byLine = {};
  for (const [k, v] of Object.entries(overrides)) {
    const [line, q] = k.split('|');
    (byLine[line] ||= []).push([q, v]);
  }
  const quarters = quarterize(applyDrivers(months, { ...drivers, volumeMult: 1, declineAdjPct: 0 })).filter((q) => q.kind === 'forecast');
  const qIndex = Object.fromEntries(quarters.map((q, i) => [q.quarter, i]));
  for (const [line, edits] of Object.entries(byLine)) {
    if (line === 'production') {
      const pts = edits.map(([q, v]) => [qIndex[q], Math.log((v * 1e6) / quarters[qIndex[q]].netMcfe)]).filter(([i, y]) => i != null && Number.isFinite(y));
      if (!pts.length) continue;
      if (pts.length === 1 || new Set(pts.map((p) => p[0])).size === 1) {
        next.volumeMult = Math.exp(pts.reduce((a, p) => a + p[1], 0) / pts.length);
        next.declineAdjPct = 0;
      } else {
        const xs = pts.map(([i]) => (i * 3) / 12);
        const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
        const my = pts.reduce((a, p) => a + p[1], 0) / pts.length;
        const sxx = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
        const slope = xs.reduce((a, x, k) => a + (x - mx) * (pts[k][1] - my), 0) / sxx;
        next.volumeMult = Math.exp(my - slope * mx);
        next.declineAdjPct = (1 - Math.exp(slope)) * 100;
      }
    } else {
      const imps = edits.map(([q, v]) => impliedDriver(months, drivers, line, q, v)).filter(Boolean);
      if (imps.length) next[imps[0].key] = imps.reduce((a, x) => a + x.value, 0) / imps.length;
    }
  }
  for (const d of DRIVERS) next[d.key] = Math.min(d.max, Math.max(d.min, +next[d.key].toFixed(4)));
  return next;
}
